import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Home, Check } from 'lucide-react';
import { User } from '../types';
import { Logo } from './Logo';

interface OnboardingProps {
  onComplete: (userData: Partial<User>) => void;
  onCancel: () => void;
}

const STEP_TITLES = ['Basics', 'Schedule', 'Activities', 'Finish'];

const WEEKDAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const SESSION_LENGTHS = [30, 45, 60, 90];

export function Onboarding({ onComplete, onCancel }: OnboardingProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<User>>({
    name: '',
    phoneNumber: '',
    fitnessGoals: {
      primary: 'general-fitness',
      weeklyWorkouts: 3,
      targetIntensity: 'moderate'
    },
    activityPreferences: [],
    weeklyTrainingVolume: 0,
    trainingDays: ['mon', 'wed', 'sat'],
    typicalSessionMinutes: 45,
    birthYear: undefined,
    weight: undefined,
    notificationPreferences: {
      sms: false,
      inApp: true
    },
    acceptedTerms: false
  });

  const activityOptions = [
    'Running',
    'Cycling',
    'Swimming',
    'Weight Training',
    'HIIT',
    'Yoga',
    'Pilates',
    'Rock Climbing',
    'CrossFit',
    'Hiking'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      if (name === 'smsOptIn') {
        setFormData(prev => ({
          ...prev,
          notificationPreferences: {
            ...prev.notificationPreferences!,
            sms: checkbox.checked
          }
        }));
      } else if (name === 'acceptedTerms') {
        setFormData(prev => ({
          ...prev,
          acceptedTerms: checkbox.checked
        }));
      }
    } else if (name === 'fitnessGoals.primary') {
      setFormData(prev => ({
        ...prev,
        fitnessGoals: {
          ...prev.fitnessGoals!,
          primary: value as User['fitnessGoals']['primary']
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? Number(value) : value
      }));
    }
  };

  const toggleTrainingDay = (day: string) => {
    setFormData((prev) => {
      const current = prev.trainingDays || [];
      return {
        ...prev,
        trainingDays: current.includes(day)
          ? current.filter((d) => d !== day)
          : [...current, day],
      };
    });
  };

  const toggleActivity = (activity: string) => {
    setFormData(prev => {
      const current = prev.activityPreferences || [];
      return {
        ...prev,
        activityPreferences: current.includes(activity)
          ? current.filter(a => a !== activity)
          : [...current, activity]
      };
    });
  };

  const handleSubmit = async () => {
    try {
      onComplete(formData);
    } catch (error) {
      console.error('Error saving onboarding data:', error);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Tell us about yourself</h2>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                We use this to tailor every recommendation to you.
              </p>
            </div>
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="input"
                placeholder="Your name"
                required
              />
            </div>
            <div>
              <label className="label">Birth year</label>
              <input
                type="number"
                name="birthYear"
                value={formData.birthYear || ''}
                onChange={handleInputChange}
                className="input"
                placeholder="1992"
                min="1920"
                max={new Date().getFullYear() - 10}
              />
              <p className="mt-1 text-xs text-zinc-400">
                Used to estimate your heart-rate zones.
              </p>
            </div>
            <div>
              <label className="label">Phone number <span className="font-normal text-zinc-400">(optional)</span></label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="input"
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Your goal & your week</h2>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                The coach plans around the days you can actually train.
              </p>
            </div>
            <div>
              <label className="label">Primary goal</label>
              <select
                name="fitnessGoals.primary"
                value={formData.fitnessGoals?.primary}
                onChange={handleInputChange}
                className="input select"
              >
                <option value="general-fitness">General Fitness</option>
                <option value="strength">Build Strength</option>
                <option value="endurance">Improve Endurance</option>
                <option value="weight-loss">Weight Loss</option>
              </select>
            </div>
            <div>
              <label className="label">Which days can you usually train?</label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => {
                  const selected = formData.trainingDays?.includes(d.key);
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => toggleTrainingDay(d.key)}
                      className={`chip ${selected ? 'chip-on' : 'chip-off'}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="label">How long is a typical session for you?</label>
              <div className="flex flex-wrap gap-2">
                {SESSION_LENGTHS.map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, typicalSessionMinutes: mins }))}
                    className={`chip ${formData.typicalSessionMinutes === mins ? 'chip-on' : 'chip-off'}`}
                  >
                    {mins} min
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-white">What do you love doing?</h2>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Pick as many as you like — recommendations lean into these.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {activityOptions.map(activity => {
                const selected = formData.activityPreferences?.includes(activity);
                return (
                  <button
                    key={activity}
                    type="button"
                    onClick={() => toggleActivity(activity)}
                    className={`chip ${selected ? 'chip-on' : 'chip-off'}`}
                  >
                    {selected && <Check className="mr-1 inline h-3.5 w-3.5" />}
                    {activity}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Almost there</h2>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                A couple of preferences and you&apos;re in.
              </p>
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
              <input
                type="checkbox"
                name="smsOptIn"
                checked={formData.notificationPreferences?.sms}
                onChange={handleInputChange}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-600"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                <span className="font-medium">Daily SMS recommendations</span>
                <span className="block text-zinc-500 dark:text-zinc-400">
                  Get each morning&apos;s workout as a text message.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
              <input
                type="checkbox"
                name="acceptedTerms"
                checked={formData.acceptedTerms}
                onChange={handleInputChange}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-600"
                required
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                I accept the{' '}
                <a href="/terms" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400" target="_blank">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400" target="_blank">
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-xl animate-fade-up">
      <div className="mb-8 flex justify-center">
        <Logo />
      </div>
      <div className="card p-8">
        <div className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onCancel}
                className="btn-ghost -ml-2 px-2"
                aria-label="Return to home"
              >
                <Home className="h-4 w-4" />
              </button>
              <h1 className="text-lg font-bold text-zinc-900 dark:text-white">Set up your profile</h1>
            </div>
            <span className="text-xs font-medium text-zinc-400">
              {STEP_TITLES[step - 1]} · {step}/4
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-800">
            <div
              className="h-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            ></div>
          </div>
        </div>

        {renderStep()}

        <div className="mt-8 flex justify-between">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="btn-ghost">
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          ) : (
            <span />
          )}
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} className="btn-primary">
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!formData.acceptedTerms}
              className="btn-primary"
            >
              Complete setup
              <Check className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
